import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useSessionStorage } from "./hooks";
import { logger, withRetry } from "./utils";

const CartContext = createContext();
export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useSessionStorage("seritonex_cart", []);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Memoized cart calculations
  const cartMetrics = useMemo(() => {
    const totalItems = cartItems.reduce(
      (sum, item) => sum + (item.quantity || 1),
      0
    );
    const totalPrice = cartItems.reduce((sum, item) => {
      const price = Number(item.price) || 0;
      const quantity = Number(item.quantity) || 1;
      return sum + price * quantity;
    }, 0);

    return {
      totalItems,
      totalPrice,
      itemCount: cartItems.length,
      isEmpty: cartItems.length === 0,
    };
  }, [cartItems]);

  // Enhanced add to cart with quantity handling
  const addToCart = useCallback(
    async (product, quantity = 1) => {
      const collectIds = (p) => {
        if (!p) return [];
        if (typeof p === "string" || typeof p === "number") return [String(p)];
        const ids = new Set();
        const candidates = [
          p.id,
          p.product_variant_id,
          p.variantId,
          p.variant_id,
          p.sku,
          p.productId,
          p.product_id,
          p._id,
        ];
        for (const c of candidates) if (c !== undefined && c !== null) ids.add(String(c));
        return Array.from(ids);
      };

      const newIds = collectIds(product);
      if (!product || newIds.length === 0) {
        setError("Invalid product data");
        return false;
      }

      try {
        setIsLoading(true);
        setError(null);

        await withRetry(async () => {
          setCartItems((prev) => {
            // Merge duplicates in prev first (defensive)
            const normalizedPrev = [];
            const seen = new Map();
            for (const it of prev) {
              const ids = collectIds(it);
              const key = ids[0] || it.id || JSON.stringify(it);
              if (seen.has(key)) {
                const idx = seen.get(key);
                normalizedPrev[idx].quantity = (normalizedPrev[idx].quantity || 1) + (it.quantity || 1);
                continue;
              }
              seen.set(key, normalizedPrev.length);
              normalizedPrev.push({ ...it });
            }

            // Find existing item that matches any id in newIds
            const matchIndex = normalizedPrev.findIndex((item) => {
              const ids = collectIds(item);
              return ids.some((id) => newIds.includes(id));
            });

            if (matchIndex >= 0) {
              const updated = [...normalizedPrev];
              updated[matchIndex] = {
                ...updated[matchIndex],
                quantity: (updated[matchIndex].quantity || 1) + quantity,
              };
              return updated;
            }

            // Add new item: ensure canonical id/productId fields are present
            const toStore = { ...product, quantity };
            toStore.id = toStore.id || newIds[0];
            if (!toStore.productId && toStore.product_id) toStore.productId = toStore.product_id;
            if (!toStore.variantId && (toStore.product_variant_id || toStore.variant_id))
              toStore.variantId = toStore.product_variant_id || toStore.variant_id;

            return [...normalizedPrev, toStore];
          });
        });

        logger.info("Product added to cart", {
          productId: product.id,
          quantity,
        });
        return true;
      } catch (error) {
        setError("Failed to add item to cart");
        logger.error("Add to cart failed", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setCartItems]
  );

  // Enhanced remove from cart
  const removeFromCart = useCallback(
    async (id) => {
      if (!id) {
        setError("Invalid product ID");
        return false;
      }

      try {
        setIsLoading(true);
        setError(null);

        await withRetry(async () => {
          const stringId = String(id);
          setCartItems((prev) => prev.filter((item) => String(item.id) !== stringId));
        });

        logger.info("Product removed from cart", { productId: id });
        return true;
      } catch (error) {
        setError("Failed to remove item from cart");
        logger.error("Remove from cart failed", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setCartItems]
  );

  // Update item quantity
  const updateQuantity = useCallback(
    async (id, quantity) => {
      if (!id || quantity < 0) {
        setError("Invalid update parameters");
        return false;
      }

      try {
        setIsLoading(true);
        setError(null);

        if (quantity === 0) {
          return await removeFromCart(id);
        }

        const stringId = String(id);
        await withRetry(async () => {
          setCartItems((prev) =>
            prev.map((item) =>
              String(item.id) === stringId ? { ...item, quantity } : item
            )
          );
        });

        logger.info("Cart quantity updated", { productId: id, quantity });
        return true;
      } catch (error) {
        setError("Failed to update quantity");
        logger.error("Update quantity failed", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setCartItems, removeFromCart]
  );

  // Clear entire cart
  const clearCart = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      await withRetry(async () => {
        setCartItems([]);
      });

      logger.info("Cart cleared");
      return true;
    } catch (error) {
      setError("Failed to clear cart");
      logger.error("Clear cart failed", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [setCartItems]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Memoized context value
  const contextValue = useMemo(
    () => ({
      cartItems,
      ...cartMetrics,
      isLoading,
      error,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      setError,
    }),
    [
      cartItems,
      cartMetrics,
      isLoading,
      error,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
